import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useAdjustPoints } from '~/hooks/apis/loyalty/useAdjustPoints';

import {
  ADJUSTMENT_FIELDS,
  type AdjustmentAction,
  type AdjustmentValues,
  EMPTY_ADJUSTMENT_VALUES,
  buildAdjustmentAmounts,
  getFieldBalance,
  getProjectedBalance,
  hasAdjustableAmount,
  hasPointsBreakdown,
  validateAdjustment,
} from '~/utils/luxLoyaltyPointsAdjustment';
import { formatDecimal } from '~/utils/numberFormatters';

/** Base + bonus point total above which the admin must confirm a second time. */
const LARGE_POINTS_THRESHOLD = 100000;

interface Props {
  open: boolean;
  onClose: () => void;
  account: App.LuxLoyaltyAccount;
  onSuccess: () => void;
}

interface BalanceTile {
  label: string;
  value: number;
}

/**
 * Read-only balance tile shown above the form, matching the KPI tile styling used on the
 * Société account overview.
 */
function renderBalanceTile({ label, value }: BalanceTile) {
  return (
    <Paper key={label} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} mt={0.5}>
        {formatDecimal(value)}
      </Typography>
    </Paper>
  );
}

export default function LuxLoyaltyAdjustPointsModal({ open, onClose, account, onSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [action, setAction] = useState<AdjustmentAction>('credit');
  const [values, setValues] = useState<AdjustmentValues>(EMPTY_ADJUSTMENT_VALUES);
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const showBreakdown = hasPointsBreakdown(account);
  const errors = useMemo(() => validateAdjustment(values, action, account), [values, action, account]);
  const hasErrors = Object.keys(errors).length > 0;

  const resetForm = () => {
    setAction('credit');
    setValues(EMPTY_ADJUSTMENT_VALUES);
    setReason('');
  };

  const adjustPointsMutation = useAdjustPoints({
    onSuccess: () => {
      enqueueSnackbar(`Points ${action === 'credit' ? 'added' : 'deducted'} successfully`, { variant: 'success' });
      resetForm();
      onSuccess();
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to adjust points: ${error.message}`, { variant: 'error' });
    },
  });

  const amounts = buildAdjustmentAmounts(values);
  const canSubmit = hasAdjustableAmount(values) && !hasErrors && !!reason.trim() && !adjustPointsMutation.isPending;

  const setFieldValue = (key: keyof AdjustmentValues, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const submitAdjustment = () => {
    adjustPointsMutation.mutate({
      customerId: account.customerId,
      action,
      ...amounts,
      source: {
        name: 'manual-adjustment',
        reason: reason.trim(),
      },
    });
  };

  const totalPointsAdjusted = (amounts.basePoints ?? 0) + (amounts.bonusPoints ?? 0);

  const handleSubmit = () => {
    if (totalPointsAdjusted > LARGE_POINTS_THRESHOLD) {
      setConfirmOpen(true);
    } else {
      submitAdjustment();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Helper text under an input: the validation error takes priority, then the
   * `800 → 700` projection once an amount is entered, then the plain balance.
   */
  const getFieldHelperText = (key: keyof AdjustmentValues, balance: number | undefined) => {
    if (errors[key]) return errors[key];

    const projected = getProjectedBalance(balance, values[key], action);
    if (projected !== undefined && balance !== undefined) {
      return `${formatDecimal(balance)} → ${formatDecimal(projected)}`;
    }

    return balance !== undefined ? `Available: ${formatDecimal(balance)}` : undefined;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        {/* DialogTitle already renders an <h2>, so keep this inline to avoid nested headings. */}
        <Typography variant="h6" component="span" fontWeight={600}>
          Adjust Points
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manually credit or debit points, status credits, and nights on this customer's Société account.
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Current balance
            </Typography>
            <Box
              data-testid="adjust-points-balances"
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: showBreakdown ? 'repeat(5, 1fr)' : 'repeat(3, 1fr)',
                },
                gap: 1.5,
              }}
            >
              {showBreakdown
                ? [
                    renderBalanceTile({ label: 'Base Points', value: account.basePoints ?? 0 }),
                    renderBalanceTile({ label: 'Bonus Points', value: account.bonusPoints ?? 0 }),
                    renderBalanceTile({ label: 'Status Credits', value: account.baseStatusCredits ?? 0 }),
                    renderBalanceTile({ label: 'Bonus Status Credits', value: account.bonusStatusCredits ?? 0 }),
                    renderBalanceTile({ label: 'Nights', value: account.numberOfNights }),
                  ]
                : [
                    renderBalanceTile({ label: 'Points', value: account.points }),
                    renderBalanceTile({ label: 'Status Credits', value: account.statusCredits }),
                    renderBalanceTile({ label: 'Nights', value: account.numberOfNights }),
                  ]}
            </Box>
            {!showBreakdown && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Base/bonus breakdown is unavailable for this account, so debits are only checked against the combined
                balance.
              </Alert>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Action *
            </Typography>
            <ToggleButtonGroup
              value={action}
              exclusive
              onChange={(_, val) => val && setAction(val)}
              size="small"
              fullWidth
            >
              <ToggleButton value="credit" color="success">
                Credit
              </ToggleButton>
              <ToggleButton value="debit" color="error">
                Debit
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            {ADJUSTMENT_FIELDS.map((field) => {
              const balance = getFieldBalance(account, field);

              return (
                <TextField
                  key={field.key}
                  label={field.label}
                  value={values[field.key]}
                  onChange={(e) => setFieldValue(field.key, e.target.value)}
                  type="number"
                  inputProps={{ min: 0, max: field.max }}
                  error={!!errors[field.key]}
                  helperText={getFieldHelperText(field.key, balance)}
                  fullWidth
                  placeholder="0"
                />
              );
            })}
          </Box>

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={3}
            placeholder="Provide a reason for this adjustment..."
          />

          <Divider />

          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
            <Box display="flex" gap={1} flexShrink={0}>
              <Button variant="text" onClick={handleClose} disabled={adjustPointsMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color={action === 'credit' ? 'success' : 'error'}
                onClick={handleSubmit}
                disabled={!canSubmit}
                data-testid="adjust-points-submit"
                endIcon={
                  adjustPointsMutation.isPending ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />
                }
                sx={{ px: 4 }}
              >
                {adjustPointsMutation.isPending ? 'Submitting...' : action === 'credit' ? 'Credit' : 'Debit'}
              </Button>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to {action} <strong>{formatDecimal(totalPointsAdjusted)} points</strong> on this account. This
            is a large adjustment — please confirm this is correct.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={action === 'credit' ? 'success' : 'error'}
            onClick={() => {
              setConfirmOpen(false);
              submitAdjustment();
            }}
          >
            Yes, {action === 'credit' ? 'Credit' : 'Debit'} Points
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
